/* eslint-disable no-script-url */
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import cx from 'classnames';
import { isIOS, isSafari, isChrome } from 'react-device-detect';
import ErrorDispatcher from '../../ErrorDispatcher/ErrorDispatcher';
import ProfileCard from '../../ProfileCard/ProfileCard';
import { actions as modalActions } from '#/redux/modules/modal/actions';
import {
  ASYNC_SWITCH_PROFILE,
  logicalSwitchToChildProfile,
  switchToParentProfile,
  checkParentalTimeLimit,
  logout,
} from '#/redux/modules/identity/actions';
import { logout as GuestLogout } from '#/redux/modules/identity/identityGuest/actions';
import {
  getSavedProfiles,
  getCurrentProfile,
  getProfileSkip,
} from '#/redux/modules/onboarding/selectors';
import {
  actions as onboardingActions,
  newDraftProfile,
} from '#/redux/modules/onboarding/actions';
import { sendScreenView } from '#/redux/modules/firestore/actions';
import {
  getSensicalTvConfig,
  getWhiteLogoUri,
  getAuthenticateUserProfileContent,
  getAgeGroupConfig,
} from '#/redux/modules/accedoOne/selectors';
import storage from '#/utils/localStorage';
import firestoreApi from '#/services/firestore/api';
import { LeftArrow, RightArrow, FocusDiv } from '#/widgets/ui';
import routes, { mainRoutes, authFlowRoutes } from '#/utils/routes';
import {
  authScreenNames,
  guestTileTitle,
  ageCategoryGroup,
  ageRanges,
  signupStates,
  GUEST_MODAL_TYPE,
} from '#/config/constants';
import GreenBackground from '#/static/images/background-images/Green.svg';
import MobileSBackground from '#/static/images/background-images/mobileS.png';
import Logo from '#/static/images/icons/logo-login.svg';
import styles from './ProfileSelection.scss';
import { isMobile, isTouchDevice } from '#/utils/componentStyleConfig';
import { GUEST_PROFILE_SELECTION } from '#/utils/navigationHelper';
import smallMonster from '#/static/images/icons/guests/small-monster.svg';
import mediumMonster from '#/static/images/icons/guests/medium-monster.svg';
import bigMonster from '#/static/images/icons/guests/big-monster.svg';
import PlaceholderProfileImage from '#/static/images/guest/contentImage-858x1872_3x.png';
import { getParentMode } from '#/redux/modules/identity/selectors';
import { isGuestMode } from '#/redux/modules/user/selectors';
import ParentZone from '#/static/images/icons/menu-parent-zone-white.svg';

const { LIVETV } = mainRoutes;

const ageGroupObj = {
  2: {
    max: 4,
    min: 2,
  },
  5: {
    max: 7,
    min: 5,
  },
  8: {
    max: 10,
    min: 8,
  },
};

// info not available in control
export const ageMetadata = {
  [`${ageCategoryGroup.MIN_AGE_TWO_UP}`]: {
    color: 'green',
    image: smallMonster,
    size: 'small',
    ageRange: ageRanges.TWO_UP,
  },
  [`${ageCategoryGroup.MIN_AGE_FIVE_UP}`]: {
    color: 'purple',
    image: mediumMonster,
    size: 'medium',
    ageRange: ageRanges.FIVE_UP,
  },
  [`${ageCategoryGroup.MIN_AGE_EIGHT_UP}`]: {
    color: 'yellow',
    image: bigMonster,
    size: 'big',
    ageRange: ageRanges.EIGHT_UP,
  },
};
const { PROFILE_CARDS_CONTAINER, PROFILE_CARD } = GUEST_PROFILE_SELECTION;

const scrollLength = 200;

const GuestCard = ({
  contentImageUrl,
  contentImageSmallUrl,
  contentImageLargeUrl,
  tileDescription,
  tileDescriptionMobile,
  tagBackgroundColor,
  labelText,
  labelTextTablet,
  labelTextMobile,
  pillPosition,
  typeOfContent,
  ageGroupValue = 0,
  selectProfile,
}) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const handleResize = () => {
    setWindowWidth(window.innerWidth);
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const renderImage = (imageUrl, label, zoneStyle) => {
    return (
      <div
        className={cx(styles.imageZone, {
          [zoneStyle]: !!imageUrl,
          [styles.isSafariBrowser]: isSafari,
          [styles.isIosChromeBrowser]: isChrome && isIOS,
        })}
      >
        <div className={styles.imageZoneCont}>
          <img
            src={imageUrl || PlaceholderProfileImage}
            alt=""
            onError={event => {
              event.currentTarget.src = PlaceholderProfileImage;
            }}
          />
        </div>
        {label && pillPosition && tagBackgroundColor && (
          <h6
            className={`${styles.tagCard} ${
              label === labelText ? styles.desktopPill : ''
            } ${label === labelTextTablet ? styles.tabletPill : ''} ${
              label === labelTextMobile ? styles.mobilePill : ''
            } ${pillPosition === 'top-left' ? styles.topLeft : ''} ${
              pillPosition === 'bottom-left' ? styles.bottomLeft : ''
            } ${pillPosition === 'top-right' ? styles.topRight : ''} ${
              pillPosition === 'bottom-right' ? styles.bottomRight : ''
            }`}
            style={{ backgroundColor: tagBackgroundColor }}
          >
            {label}
          </h6>
        )}
      </div>
    );
  };
  return (
    <div aria-label={`${tileDescription}.`} className={styles.card}>
      <div
        className={`${styles.cardTitle} ${
          isSafari ? styles.isSafariBrowser : ''
        }`}
        onClick={() => selectProfile(typeOfContent, ageGroupValue)}
      >
        {windowWidth > 1024 &&
          renderImage(contentImageUrl, labelText, styles.desktopImageZone)}
        {windowWidth <= 1024 &&
          windowWidth > 480 &&
          renderImage(
            contentImageLargeUrl,
            labelTextTablet,
            styles.largeImageZone,
          )}
        {windowWidth <= 480 &&
          renderImage(
            contentImageSmallUrl,
            labelTextMobile,
            styles.smallImageZone,
          )}
        {tileDescription && (
          <h2 className={styles.desktopLabel}>{tileDescription}</h2>
        )}
        {tileDescriptionMobile && (
          <h2 className={styles.mobileLabel}>{tileDescriptionMobile}</h2>
        )}
      </div>
    </div>
  );
};

GuestCard.propTypes = {
  contentImageUrl: PropTypes.string,
  contentImageLargeUrl: PropTypes.string,
  contentImageSmallUrl: PropTypes.string,
  tileDescription: PropTypes.string,
  tileDescriptionMobile: PropTypes.string,
  tagBackgroundColor: PropTypes.string,
  labelText: PropTypes.string,
  labelTextTablet: PropTypes.string,
  labelTextMobile: PropTypes.string,
  pillPosition: PropTypes.string,
  typeOfContent: PropTypes.string,
  ageGroupValue: PropTypes.number,
  selectProfile: PropTypes.func,
};

const Cards = ({
  history,
  authenticateUserProfileContent,
  profiles,
  addProfile,
}) => {
  const dispatch = useDispatch();
  const ageGroupConfig = useSelector(getAgeGroupConfig);
  const onSwitch = useCallback(async (profileId, type) => {
    const welcomeModal = JSON.parse(storage.get('welcomeModal')) || {};
    const timeLimitReached = await dispatch(
      checkParentalTimeLimit({ profileId }),
    );
    if (!timeLimitReached) {
      const shouldReload =
        !(`${profileId}` in welcomeModal) ||
        JSON.parse(storage.get('welcomeModal'))?.[`${profileId}`]
          .showWelcomeModal;

      await dispatch(logicalSwitchToChildProfile(profileId));
      if (type !== 'live') {
        history.push(routes.HOME);
        if (!shouldReload) {
          window?.location.reload();
        }
      }
      dispatch(modalActions.closeModal());
    }
  }, []);
  const selectProfile = useCallback(
    async (type, age) => {
      if (type === 'live') {
        const pathname = `${LIVETV}/${age}`;
        const newEntry = {
          pathname,
          state: { fromProfileSelectionPage: routes.PROFILE_SELECTION },
        };
        history.replace('/');
        history.push(newEntry);
      } else {
        const selectedAgeGroup = ageGroupObj[age];
        if (!selectedAgeGroup) {
          return;
        }
        const reducedProfiles = profiles.filter(profile => {
          const {
            parentalTimeLimit = 0,
            timeWatched: { duration: durationWatched = 0 } = {},
          } = profile;

          const timeLimitReached =
            parentalTimeLimit !== 0 && durationWatched > parentalTimeLimit;

          return (
            profile.age >= selectedAgeGroup.min &&
            profile.age <= selectedAgeGroup.max &&
            !timeLimitReached
          );
        });
        if (reducedProfiles.length === 1) {
          const getFirstProfileId = reducedProfiles[0].id;
          onSwitch(getFirstProfileId, type, age);
        } else if (reducedProfiles.length > 0) {
          dispatch(
            modalActions.openModal({
              modalId: 'ProfileToWatch',
              modalData: {
                type,
                age,
                onSwitch,
                reducedProfiles,
              },
            }),
          );
        } else {
          await dispatch(newDraftProfile());
          dispatch(
            modalActions.openModal({
              modalId: 'ProfileToWatchNone',
              modalData: {
                addProfile,
                age,
                name: `Guest ${age}up`,
              },
            }),
          );
        }
      }
    },
    [addProfile, dispatch, history, onSwitch, profiles],
  );
  const guestCards = useMemo(() => {
    const getAllKeyEntriesByContent = Object.keys(
      authenticateUserProfileContent.setAllEntriesByContent,
    );
    const getAgeGroupConfigValues = Object.values(ageGroupConfig || []);

    return Object.values(
      authenticateUserProfileContent.setAllEntriesByContent ?? {},
    ).map((groupContent, index, array) => {
      return (
        <div
          className={styles.guestTitleText}
          key={getAllKeyEntriesByContent[index]}
        >
          <h2 className={styles.guestHorizontalLine}>
            {guestTileTitle[getAllKeyEntriesByContent[index]]}
          </h2>
          <div
            className={cx({ [styles.isIosChromeBrowser]: isChrome && isIOS })}
          >
            {groupContent.map((group, idx) => {
              const {
                ageGroupValue,
                contentImageUrl,
                contentImageLargeUrl,
                contentImageSmallUrl,
                tileDescription,
                tileDescriptionMobile,
                title,
                typeOfContent,
                tagBackgroundColor,
                labelText,
                labelTextTablet,
                labelTextMobile,
                pillPosition,
              } = group;
              const {
                minAge = '',
                maxAge = '',
                noRegistrationConfig: { info1, info2, info3 } = {},
              } =
                (ageGroupValue
                  ? getAgeGroupConfigValues.find(
                      c => c.ageGroupValue === ageGroupValue,
                    )
                  : {}) || {};
              const ageRange = minAge && maxAge ? `${minAge}-${maxAge}` : '';
              const cardIndex = index * (array.length + 1) + idx;
              return (
                <GuestCard
                  history={history}
                  key={cardIndex}
                  {...{
                    ageGroupValue,
                    contentImageUrl,
                    contentImageLargeUrl,
                    contentImageSmallUrl,
                    tileDescription,
                    tileDescriptionMobile,
                    title,
                    typeOfContent,
                    cardIndex,
                    ageRange,
                    info1,
                    info2,
                    info3,
                    tagBackgroundColor,
                    labelText,
                    labelTextTablet,
                    labelTextMobile,
                    pillPosition,
                    selectProfile,
                  }}
                />
              );
            })}
          </div>
        </div>
      );
    });
  }, [ageGroupConfig, authenticateUserProfileContent, selectProfile]);

  return (
    <FocusDiv
      nav={{
        id: PROFILE_CARDS_CONTAINER,
        forwardFocus: `${PROFILE_CARD}-0`,
      }}
      className={styles.cardsArea}
    >
      {guestCards}
    </FocusDiv>
  );
};
Cards.propTypes = {
  history: PropTypes.object,
  authenticateUserProfileContent: PropTypes.object,
  profiles: PropTypes.array,
  addProfile: PropTypes.func,
};

const GuestCards = ({ history, profiles, addProfile }) => {
  const authenticateUserProfileContent = useSelector(
    getAuthenticateUserProfileContent,
  );
  return (
    <>
      <Cards
        history={history}
        authenticateUserProfileContent={authenticateUserProfileContent}
        profiles={profiles}
        addProfile={addProfile}
      />
    </>
  );
};

GuestCards.propTypes = {
  history: PropTypes.object,
  profiles: PropTypes.array,
  addProfile: PropTypes.func,
};
const ProfileSelection = ({ history, onSwitchEnd = null }) => {
  const [hideSpinner] = useState(false);
  const dispatch = useDispatch();
  const { logoUri = Logo } = useSelector(getSensicalTvConfig) || {};
  const backgroundImage = isMobile() ? MobileSBackground : GreenBackground;
  const whiteLogoUri = useSelector(getWhiteLogoUri) || '';

  // Scrolling
  const baseballCardsContainer = useRef(null);
  const [scrollLeftPosition, setScrollLeftPosition] = useState(0);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);
  // const [pinGateRedirectOptions, setPinGateRedirectOptions] = useState({});

  const [editMode] = useState(false);
  const currentProfile = useSelector(getCurrentProfile);
  const profiles = useSelector(getSavedProfiles);
  const parentMode = useSelector(getParentMode);
  const guestModeIs = useSelector(isGuestMode);
  const profileSkip = useSelector(getProfileSkip);

  const editAvatar = useCallback(id => {
    dispatch(onboardingActions.editProfile({ profileId: id }));
    dispatch(
      modalActions.openModal({
        modalId: 'AvatarModal',
        successAction: {
          type: String(onboardingActions.setProfileAvatar),
        },
        finallyAction: {
          type: String(onboardingActions.discardProfile),
        },
      }),
    );
  }, []);

  const addProfileOld = useCallback(() => {
    dispatch(
      modalActions.openModal({
        modalId: 'PinModal',
        modalData: {
          isNewAccountMobile: false,
        },
        successAction: async pin => {
          await dispatch(switchToParentProfile({ credential: pin }));
          await dispatch(newDraftProfile());
          history.push(routes.PROFILE_ADDITION);
        },
      }),
    );
  }, []);

  const addProfile = useCallback((name, avatar) => {
    dispatch(
      modalActions.openModal({
        modalId: 'PinModal',
        modalData: {
          isNewAccountMobile: false,
        },
        successAction: async pin => {
          const newEntry = {
            pathname: routes.PROFILE_ADDITION,
            state: {
              name,
              avatar,
            },
          };
          await dispatch(switchToParentProfile({ credential: pin }));
          await dispatch(newDraftProfile());
          history.push(newEntry);
        },
      }),
    );
  }, []);

  const onSwitch = useCallback(async profileId => {
    const welcomeModal = JSON.parse(storage.get('welcomeModal')) || {};
    const timeLimitReached = await dispatch(
      checkParentalTimeLimit({ profileId }),
    );
    if (!timeLimitReached) {
      const shouldReload =
        !(`${profileId}` in welcomeModal) ||
        JSON.parse(storage.get('welcomeModal'))?.[`${profileId}`]
          .showWelcomeModal;

      await dispatch(logicalSwitchToChildProfile(profileId));
      onSwitchEnd ? onSwitchEnd(profileId) : history.push(routes.HOME);
      if (!shouldReload) {
        window?.location.reload();
      }
    }
  }, []);

  useEffect(() => {
    if (profiles.length > 0 && profileSkip) {
      const skipUser = profiles.find(p => p.name === profileSkip);
      if (skipUser) {
        dispatch(onboardingActions.setProfileSkip(''));
        onSwitch(skipUser.id);
      }
    }
  }, [dispatch, onSwitch, profileSkip, profiles]);

  const baseballCards = useMemo(() => {
    const reducedProfiles = profiles.map(profile => {
      const {
        parentalTimeLimit = 0,
        timeWatched: { duration: durationWatched = 0 } = {},
      } = profile;

      const timeLimitReached =
        parentalTimeLimit !== 0 && durationWatched > parentalTimeLimit;

      return (
        <ProfileCard
          locked={timeLimitReached}
          key={profile.id}
          profile={profile}
          editMode={editMode}
          onSwitch={() => {
            onSwitch(profile.id);
          }}
          onEdit={() => editAvatar(profile.id)}
          className={styles.profileNames}
        />
      );
    });
    reducedProfiles.push(
      <ProfileCard
        locked={false}
        key={-1}
        profile={{ id: -1, name: 'Add New', avatar: null }}
        isDummy
        parentClick={addProfileOld}
        className={styles.profileNames}
      />,
    );
    return reducedProfiles;
  }, [profiles, currentProfile, editMode]);

  // Initialize timesup data
  useEffect(() => {
    profiles.forEach(({ id: profileId }) => {
      dispatch(checkParentalTimeLimit({ profileId, showModal: false }));
    });
  }, []);

  useEffect(() => {
    dispatch(onboardingActions.showGuestProfileImage(false));
    return () => {
      dispatch(onboardingActions.showGuestProfileImage(false));
    };
  }, []);

  // Scrolling
  const scrollAction = useCallback(
    ({ back = false } = {}) => {
      if (back) {
        baseballCardsContainer.current.scrollLeft -= scrollLength;
      } else {
        baseballCardsContainer.current.scrollLeft += scrollLength;
      }
    },
    [baseballCardsContainer],
  );

  useEffect(() => {
    firestoreApi.updateDefaultUserProperties({
      age_group: 'parent',
    });
    dispatch(sendScreenView(authScreenNames.PROFILE_SELECTION));

    const handler = () => {
      setScrollLeftPosition(baseballCardsContainer.current.scrollLeft);
    };
    baseballCardsContainer.current.addEventListener('scroll', handler);
    return () => {
      baseballCardsContainer.current.removeEventListener('scroll', handler);
    };
  }, []);

  const showScrollingEffect = useCallback(() => {
    const {
      scrollWidth,
      scrollLeft,
      offsetWidth,
    } = baseballCardsContainer.current;
    // Container is fully visible, no scrolling
    if (scrollWidth - offsetWidth <= 55) {
      setShowRight(false);
      setShowLeft(false);
      baseballCardsContainer.current.scrollLeft =
        scrollLeft > 0 ? -1 * scrollLeft : 1 * scrollLeft;
      return;
    }

    // Right arrow visibility
    if (scrollWidth - scrollLeft > offsetWidth) {
      if (!isTouchDevice()) {
        setShowRight(true);
      }
    } else {
      setShowRight(false);
    }

    // Left arrow visibility
    if (scrollLeftPosition === 0 && scrollLeft === 0) {
      setShowLeft(false);
    } else if (!isTouchDevice()) {
      setShowLeft(true);
    }
  }, [scrollLeftPosition, profiles]);

  useEffect(() => {
    showScrollingEffect();
  }, [scrollLeftPosition, profiles]);

  useEffect(() => {
    const handler = () => {
      showScrollingEffect();
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const onSwitchToParentZone = useCallback(() => {
    if (guestModeIs) {
      dispatch(
        modalActions.openModal({
          modalId: 'GuestProfileModal',
          modalData: {
            modalType: GUEST_MODAL_TYPE.parentZoneBlocked,
          },
          successAction: () => {
            dispatch(
              onboardingActions.setSignupState({
                signupState: signupStates.CREATE_ACCOUNT,
              }),
            );
            dispatch(GuestLogout(authFlowRoutes.ACCOUNT_CREATION));
          },
        }),
      );
    } else if (isTouchDevice()) {
      dispatch(
        modalActions.openModal({
          modalId: 'MobileParentZone',
          successAction: () => {
            dispatch(logout());
            dispatch(modalActions.closeModal());
          },
        }),
      );
    } else {
      dispatch(
        modalActions.openModal({
          modalId: 'PinModal',
          modalData: {
            isNewAccountMobile: false,
          },
          successAction: async pin => {
            await dispatch(switchToParentProfile({ credential: pin }));
            history.push(mainRoutes.PARENT_ZONE);
          },
        }),
      );
    }
  }, [parentMode, guestModeIs]);

  return (
    <div
      className={styles.profileSelection}
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      <ErrorDispatcher errorId={ASYNC_SWITCH_PROFILE} />
      <div className={styles.heading}>
        <img
          className={styles.logoWebLayout}
          src={whiteLogoUri || logoUri}
          style={{ display: logoUri || whiteLogoUri ? 'inline-block' : 'none' }}
          loading="eager"
          alt="Logo"
        />
        <div
          className={styles.parentalZoneContLarge}
          onClick={onSwitchToParentZone}
        >
          <span>
            <img
              className={styles.settingsIcon}
              src={ParentZone}
              alt="Settings"
            />
          </span>{' '}
          Parent Zone
        </div>
      </div>
      <div className={styles.parentalZoneCont} onClick={onSwitchToParentZone}>
        <span>
          <img
            className={styles.settingsIcon}
            src={ParentZone}
            alt="Settings"
          />
        </span>{' '}
        Parent Zone
      </div>
      <h2 className={styles.guestHorizontalLineProfiles}>Profiles</h2>
      <div
        ref={baseballCardsContainer}
        className={styles.baseballCardsContainer}
      >
        {!isMobile() && <LeftArrow visible={showLeft} {...{ scrollAction }} />}
        {baseballCards}
        {!isMobile() && (
          <RightArrow visible={showRight} {...{ scrollAction }} />
        )}
      </div>
      <GuestCards
        history={history}
        showButtons={hideSpinner}
        profiles={profiles}
        addProfile={addProfile}
      />
    </div>
  );
};

ProfileSelection.propTypes = {
  history: PropTypes.object,
  onSwitchEnd: PropTypes.func,
};

export default withRouter(ProfileSelection);
